/**
 * WHAT: Hooks for running recurring callbacks on a timer with automatic
 *       cleanup (useSafeInterval, useConditionalInterval).
 * WHY:  DAMA pages use polling intervals to refresh campaign status, recent
 *       executions, and compliance data. Native setInterval leaks if not
 *       cleared on unmount, and updating the callback reference without
 *       re-creating the interval is tricky. These hooks solve both problems.
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * WHAT: Runs a callback at a fixed interval (or pauses when delay is null)
 *       and clears the interval automatically on unmount.
 * WHY:  Provides a leak-proof setInterval that also handles callback identity
 *       changes via a ref, so the interval is only reset when the delay
 *       changes -- not on every render.
 *
 * useSafeInterval - A hook that provides a safe setInterval that automatically
 * cleans up when the component unmounts and handles callback updates properly.
 *
 * @param {Function} callback - The function to call on each interval
 * @param {number|null} delay - Delay in ms, or null to pause the interval
 * @param {boolean} immediate - If true, run callback immediately on mount
 *
 * @example
 * // Basic usage - refresh data every 5 seconds
 * useSafeInterval(() => {
 *   fetchData();
 * }, 5000);
 *
 * // Conditional interval - only run when tab is active
 * useSafeInterval(
 *   () => fetchData(),
 *   isActiveTab ? 5000 : null  // null pauses the interval
 * );
 *
 * // Run immediately on mount, then every 5 seconds
 * useSafeInterval(() => fetchData(), 5000, true);
 */
export function useSafeInterval(callback, delay, immediate = false) {
  const savedCallback = useRef(callback);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    isMountedRef.current = true;

    // Don't schedule if delay is null
    if (delay === null) {
      return;
    }

    // Run immediately if requested
    if (immediate && isMountedRef.current) {
      savedCallback.current();
    }

    intervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        savedCallback.current();
      }
    }, delay);

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [delay, immediate]);

  // Return a function to manually clear the interval
  const clearSafeInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return clearSafeInterval;
}

/**
 * WHAT: Thin wrapper around useSafeInterval that accepts a boolean `shouldRun`
 *       flag instead of requiring the caller to pass `null` for the delay.
 * WHY:  Makes intent clearer at the call site -- e.g.,
 *       `useConditionalInterval(refresh, 5000, activeTab === 'executions')`
 *       reads more naturally than computing a nullable delay.
 *
 * useConditionalInterval - A hook for intervals that should only run under
 * certain conditions (e.g., when a specific tab is active).
 *
 * @param {Function} callback - The function to call on each interval
 * @param {number} delay - Delay in milliseconds
 * @param {boolean} shouldRun - Whether the interval should be running
 *
 * @example
 * // Only refresh when on the 'executions' tab
 * useConditionalInterval(
 *   () => loadRecentExecutions(),
 *   5000,
 *   activeTab === 'executions'
 * );
 */
export function useConditionalInterval(callback, delay, shouldRun) {
  return useSafeInterval(callback, shouldRun ? delay : null);
}

export default useSafeInterval;
